import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Hub, I18n } from 'aws-amplify';
import { translations } from '@aws-amplify/ui-react';
import ReCaptcha from "../../components/generic/captcha";
import { Authenticator, CheckboxField, TextField, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { connect } from 'react-redux';
import { signOut } from '../../redux/actions/users';
import { RevAlert, RevButton, RevProgress, RevSegmented } from "../../components/ds";
import logoDark from "../../components/navigation/revopic-logo-dark.svg";
import playEs from "./badges/google-play-es.png";
import playEn from "./badges/google-play-en.png";
import appStoreEs from "./badges/app-store-es.svg";
import appStoreEn from "./badges/app-store-en.svg";
I18n.putVocabularies(translations);

const env = process.env.REACT_APP_ENVIROMENT;

const socialProviders = !env || env === 'prod'
  ? ['google']
  : ['google', 'apple'];

const LANG_ITEMS = [{ value: 'es', label: 'ES' }, { value: 'en', label: 'EN' }];

const TERMS_URL = "https://revopic.com/terms-and-conditions/";
const PRIVACY_URL = "https://revopic.com/privacy-policy/";
const HELP_URL = "https://revopic.com/help";
const PLAY_URL = "https://play.google.com/store/apps/details?id=com.nuonet.revopic00";
const APPSTORE_URL = "https://apps.apple.com/es/app/revopic/id6477912416";
// Badges oficiales de las tiendas, en local (los de revopic.com/wp-content devolvian HTML).
const STORE_BADGES = {
    es: { play: playEs, appStore: appStoreEs },
    en: { play: playEn, appStore: appStoreEn },
};

// Las tres unicas rutas que `initialState` del Authenticator acepta
// (@aws-amplify/ui/dist/types/types/authenticator/stateMachine/context.d.ts:30).
const SAFE_INITIAL_ROUTES = ['signIn', 'signUp', 'resetPassword'];

// Amplify congela las etiquetas y los placeholders de `formFields` al inicializar
// su maquina de estados y no los vuelve a leer: `useAuthenticatorInitMachine.mjs`
// llama a `initializeMachine(data)` una sola vez, al pasar por `setup`, con un
// `useRef` de guarda, y `shared/FormFields.mjs` memoriza ademas los elementos ya
// creados en otro `useRef`. La unica forma de que el selector de idioma llegue a
// esos textos es remontar el Authenticator; para no devolver al usuario a Entrar,
// el contenedor necesita saber en que ruta esta la maquina, y este contexto es el
// canal: el slot `Header` (que Amplify pinta en TODAS las vistas, via
// RouteContainer) la publica hacia arriba.
const RouteSyncContext = createContext(null);

const AuthHead = () => {
    const { t } = useTranslation();
    const { route } = useAuthenticator((context) => [context.route]);
    const publishRoute = useContext(RouteSyncContext);
    useEffect(() => {
        if (publishRoute) publishRoute(route);
    }, [publishRoute, route]);
    const heads = {
        signIn: { title: t('auth.title.signIn'), sub: t('auth.sub.signIn') },
        signUp: { title: t('auth.title.signUp'), sub: t('auth.sub.signUp') },
        resetPassword: { title: t('auth.title.resetPassword'), sub: t('auth.sub.resetPassword') },
    };
    const head = heads[route];
    if (!head) return null;
    return (
        <div className="rev-auth__head">
            <h2 className="rev-auth__h2">{head.title}</h2>
            <p className="rev-auth__lead">{head.sub}</p>
        </div>
    );
};

const SignInFoot = () => {
    const { t, i18n } = useTranslation();
    const badgeLang = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);
    const badges = STORE_BADGES[badgeLang] || STORE_BADGES.es;
    const { toResetPassword } = useAuthenticator((context) => [context.toResetPassword]);
    const footRef = useRef(null);
    const [fieldset, setFieldset] = useState(null);

    useLayoutEffect(() => {
        const host = footRef.current && footRef.current.parentElement;
        setFieldset(host ? host.querySelector('form[data-amplify-authenticator-signin] fieldset') : null);
    }, []);

    const row = (
        <div className="rev-auth__row">
            <button type="button" className="rev-auth__link" onClick={toResetPassword}>{t('auth.forgot')}</button>
        </div>
    );

    return (
        <div className="rev-auth__signfoot" ref={footRef}>
            {fieldset ? createPortal(row, fieldset) : row}
            <div className="rev-auth__app">
                <strong>{t('auth.app.title')}</strong>
                <span>{t('auth.app.text')}</span>
                <div className="rev-auth__badges">
                    <a href={PLAY_URL} target="_blank" rel="noopener noreferrer" className="rev-auth__badge">
                        <img
                            src={badges.play}
                            alt="Google Play"
                            className="rev-auth__badge-img"
                        />
                    </a>
                    <a href={APPSTORE_URL} target="_blank" rel="noopener noreferrer" className="rev-auth__badge">
                        <img
                            src={badges.appStore}
                            alt="App Store"
                            className="rev-auth__badge-img"
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

const CheckboxFieldNews = () => {
    const { t } = useTranslation();
    const [checked, setChecked] = useState(false);
    return (
        <CheckboxField
            name="custom:ReceiveNews"
            value={checked}
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            label={t("I want to receive Revopic news")}
        />
    );
};

const UTMFields = () => {
    const location = useLocation();
    const [utmSource, setUtmSource] = useState("");
    const [utmMedium, setUtmMedium] = useState("");
    const [utmCampaign, setUtmCampaign] = useState("");

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has('utm_source')) setUtmSource(searchParams.get('utm_source'));
        if (searchParams.has('utm_medium')) setUtmMedium(searchParams.get('utm_medium'));
        if (searchParams.has('utm_campaign')) setUtmCampaign(searchParams.get('utm_campaign'));
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <TextField
                name="custom:utm_source"
                className="rev-auth__hidden"
                value={utmSource}
                onChange={() => null}
            />
            <TextField
                name="custom:utm_medium"
                className="rev-auth__hidden"
                value={utmMedium}
                onChange={() => null}
            />
            <TextField
                name="custom:utm_campaign"
                className="rev-auth__hidden"
                value={utmCampaign}
                onChange={() => null}
            />
            <TextField
                name="custom:is_photographer"
                className="rev-auth__hidden"
                value="true"
                onChange={() => null}
            />
        </>
    );
};

const SignUpFields = () => (
    <>
        <Authenticator.SignUp.FormFields />
        <CheckboxFieldNews />
        <UTMFields />
    </>
);

const SignUpFoot = () => {
    const { t } = useTranslation();
    return (
        <p className="rev-auth__legal">
            {t('By signing up you agree to the')}{' '}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">{t('Terms of Use')}</a>{' '}
            {t('and')}{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">{t('Privacy Policy')}</a>
        </p>
    );
};

const NoHead = () => null;

// Cognito avisa del correo duplicado de dos formas: UsernameExistsException si ya es
// usuario nativo del pool, y UserLambdaValidationException («PreSignUp failed with error
// Email already exists: …») si el trigger lo encuentra en Dynamo (cuenta creada con Google
// o desde la app). Sin esto, todo fallo de alta se pintaba como «Credenciales inválidas».
const isAlreadyRegisteredError = (error) => {
    if (!error) return false;
    const code = error.code || error.name || '';
    const message = typeof error === 'string' ? error : (error.message || '');
    return code === 'UsernameExistsException' || /already exists/i.test(message);
};

const AUTH_COMPONENTS = {
    Header: AuthHead,
    SignIn: { Footer: SignInFoot },
    SignUp: { FormFields: SignUpFields, Footer: SignUpFoot },
    ResetPassword: { Header: NoHead },
};

const CognitoLanding = (props) => {
    const [captchaOk, setCaptchaOk] = useState(null);
    const [simulatedError, setSimulatedError] = useState(undefined);
    const location = useLocation();
    const { t, i18n, ready } = useTranslation();
    const rawLang = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);
    const lang = ['es', 'en'].includes(rawLang) ? rawLang : 'es';
    const [, bumpVocab] = useState(0);
    // Idioma con el que esta montado el Authenticator (ver RouteSyncContext).
    const [authLang, setAuthLang] = useState(lang);
    // Ruta viva de la maquina de Amplify, publicada por el slot Header.
    const [machineRoute, setMachineRoute] = useState(null);
    const publishRoute = useCallback((route) => setMachineRoute(route), []);
    const handleCaptcha = useCallback((result) => {
        if (result === false) {
            setCaptchaOk(false);
        } else if (result === true || result) {
            setCaptchaOk(true);
        }
    }, []);
    // Red de seguridad: si el diccionario no llegara, la pantalla se pinta igual
    // (con las claves a la vista, degradado) antes que dejar el acceso bloqueado.
    const [i18nStalled, setI18nStalled] = useState(false);

    useEffect(() => {
        if (ready) return undefined;
        const timer = setTimeout(() => setI18nStalled(true), 5000);
        return () => clearTimeout(timer);
    }, [ready]);

    // El Authenticator se remonta al cambiar de idioma, con la vista actual como
    // `initialState`, para que las etiquetas congeladas se relean. No se remonta
    // si la maquina esta dentro de un subflujo (confirmar codigo, TOTP, contrasena
    // nueva, verificar usuario): esas rutas no son `initialState` validos y tirar
    // un paso a medias es peor que la etiqueta en el idioma anterior.
    // Un cambio de idioma pendiente entra en cuanto la maquina vuelve a una ruta
    // segura, de ahi que `machineRoute` sea dependencia.
    useEffect(() => {
        if (!ready || authLang === lang) return;
        if (machineRoute === null || SAFE_INITIAL_ROUTES.includes(machineRoute)) setAuthLang(lang);
    }, [ready, lang, authLang, machineRoute]);

    useEffect(() => {
        if (!ready) return;
        I18n.putVocabulariesForLanguage(lang, {
            'Sign In': t('auth.amplify.signInTab'),
            'Sign in': t('auth.amplify.signInButton'),
            'Create Account': t('auth.amplify.createAccount'),
            'Back to Sign In': t('auth.amplify.backToSignIn'),
            'Send code': t('auth.amplify.sendCode'),
            'or': t('auth.amplify.or'),
            'Sign In with Google': t('auth.amplify.signInGoogle'),
            'Sign In with Apple': t('auth.amplify.signInApple'),
            'Sign Up with Google': t('auth.amplify.signUpGoogle'),
            'Sign Up with Apple': t('auth.amplify.signUpApple'),
        });
        I18n.setLanguage(lang);
        bumpVocab((n) => n + 1);
    }, [lang, t, ready]);

    useEffect(() => {
        const params = location.search;
        if (params.split('=')[1] === 'mail-already-registered') {
            setTimeout(() => {
                setSimulatedError("User already registered");
            }, 50); // Esperar a Amplify
        }
    }, [location, t]);

    useEffect(() => {
        const listener = (data) => {
            if (data.payload.event === 'signIn_failure') {
                setTimeout(() => {
                    const errorElement = document.querySelector('.amplify-alert--error .amplify-alert__body');
                    if (errorElement) {
                        errorElement.textContent = t('Credentials not valid');
                    }
                }, 50); // Esperar a Amplify
            }
            if (data.payload.event === 'signUp_failure') {
                const text = isAlreadyRegisteredError(data.payload.data)
                    ? t('User already registered')
                    : t('Credentials not valid');
                setTimeout(() => {
                    const errorElement = document.querySelector('.amplify-alert--error .amplify-alert__body');
                    if (errorElement) {
                        errorElement.textContent = text;
                    }
                }, 50); // Esperar a Amplify
            }
            if (data.payload?.data?.toString().includes("InvalidPasswordException: Password did not conform with policy: Password not long enough")) {
                setTimeout(() => {
                    const errorElement = document.querySelector('.amplify-alert--error .amplify-alert__body');
                    if (errorElement) {
                        errorElement.textContent = t('Password not long enough, min 8 characters');
                    }
                }, 50); // Esperar a Amplify
            }
        };
        Hub.listen('auth', listener);
        return () => Hub.remove('auth', listener);
    }, [t]);

    const formFields = {
        signUp: {
            name: {
                placeholder: t('Enter your Name'),
                label: t('Name'),
                order: 1
            },
            email: {
                label: t('auth.email'),
                placeholder: t('auth.emailPlaceholder'),
                order: 2,
                onChange: (e) => {
                    const start = e.target.selectionStart;
                    const end = e.target.selectionEnd;
                    const value = e.target.value.toLowerCase();

                    // Only update if the value is actually different
                    if (e.target.value !== value) {
                        e.target.value = value;
                        // Restore cursor position after value change
                        // This needs to be scheduled after the current event loop
                        setTimeout(() => {
                            e.target.setSelectionRange(start, end);
                        }, 0);
                    }
                }
            },
            // Amplify antepone el prefijo del selector (country_code) y manda E.164 a Cognito.
            phone_number: {
                label: t('auth.phone'),
                placeholder: t('auth.phonePlaceholder'),
                dialCode: '+34',
                isRequired: true,
                order: 3
            },
            password: {
                label: t('Password'),
                placeholder: t('auth.passwordHint'),
                order: 4
            },
            confirm_password: {
                label: t('Confirm Password'),
                placeholder: t('Enter your password again'),
                order: 5
            }
        },
        signIn: {
            username: {
                label: t('auth.email'),
                placeholder: t('auth.emailPlaceholder'),
                order: 1,
                onChange: (e) => {
                    const start = e.target.selectionStart;
                    const end = e.target.selectionEnd;
                    const value = e.target.value.toLowerCase();

                    // Only update if the value is actually different
                    if (e.target.value !== value) {
                        e.target.value = value;
                        // Restore cursor position after value change
                        setTimeout(() => {
                            e.target.setSelectionRange(start, end);
                        }, 0);
                    }
                }
            },
            password: {
                label: t('Password'),
                placeholder: t('auth.passwordHint'),
                order: 2,
                required: true
            }
        }
    }

    const SimulatedAmplifyError = () => {
        if (!simulatedError) return null;

        return (
            <div className="amplify-alert amplify-alert--error">
                <div className="amplify-alert__body">
                    {t(simulatedError)}
                </div>
            </div>
        );
    };

    // Sin diccionario cargado, `t()` devuelve la clave: el Authenticator la
    // congelaria en sus campos para toda la sesion (ver RouteSyncContext), asi que
    // no se monta nada hasta tener los textos.
    if (!ready && !i18nStalled) {
        return <div className="rev-page"><RevProgress /></div>;
    }

    const initialRoute = SAFE_INITIAL_ROUTES.includes(machineRoute)
        ? machineRoute
        : location.pathname.slice(1);
    // Si la pantalla se pinto degradada (red de seguridad), el Authenticator
    // habria congelado las claves crudas: al llegar el diccionario, remonta.
    const authKey = ready ? authLang : 'stalled';

    return (
        <div className="rev-auth">
            <aside className="rev-auth__aside" data-register="dark">
                <span className="rev-auth__filo" />
                <img src={logoDark} alt="Revopic" className="rev-logo--on-dark rev-auth__logo" />
                <div className="rev-auth__claim">
                    <span className="rev-eyebrow">{t('auth.eyebrow')}</span>
                    <h1 className="rev-auth__h1">{t('auth.claim')}</h1>
                    <p className="rev-auth__sub">{t('auth.claimSub')}</p>
                    <ul className="rev-auth__bullets">
                        <li>{t('auth.bullet1')}</li>
                        <li>{t('auth.bullet2')}</li>
                        <li>{t('auth.bullet3')}</li>
                    </ul>
                </div>
                <p className="rev-auth__brand rev-mono">{t('auth.brand')}</p>
            </aside>
            <main className="rev-auth__main">
                <div className="rev-auth__top">
                    <RevSegmented
                        className="rev-auth__lang"
                        items={LANG_ITEMS}
                        value={lang}
                        onChange={(value) => i18n.changeLanguage(value)}
                        role="group"
                        aria-label={t('account.basicData.lang.label')}
                    />
                </div>
                <div className="rev-auth__form">
                    {/* Load ReCaptcha only once and keep it mounted */}
                    {captchaOk === null && (
                        <ReCaptcha action="SIGNUP" callback={handleCaptcha} />
                    )}
                    {captchaOk === false
                        ? <RevAlert tone="err">{t('auth.captchaError')}</RevAlert>
                        : <>
                            <RouteSyncContext.Provider value={publishRoute}>
                                <Authenticator
                                    key={authKey}
                                    initialState={initialRoute}
                                    formFields={formFields}
                                    loginMechanisms={['email']}
                                    socialProviders={socialProviders}
                                    signUpAttributes={['name', 'phone_number']}
                                    components={AUTH_COMPONENTS}
                                >
                                    <div className="rev-auth__signed">
                                        <RevButton variant="secondary" fit onClick={() => {
                                            props.signOut();
                                        }}>{t('nav.menu.signOut')}</RevButton>
                                    </div>
                                </Authenticator>
                            </RouteSyncContext.Provider>
                            <SimulatedAmplifyError />
                        </>}
                </div>
                <div className="rev-auth__foot">
                    <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">{t('Terms of Use')}</a>
                    <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">{t('auth.foot.privacy')}</a>
                    <a href={HELP_URL} target="_blank" rel="noopener noreferrer">{t('nav.menu.help')}</a>
                </div>
            </main>
        </div>
    );

};

const mapDispatchToProps = {
    signOut
};

export default connect(null, mapDispatchToProps)(CognitoLanding);
